import { useState } from "owa-react";
import React from "react";
import loc, { format } from "owa-localize";
import { observer } from "owa-mobx-react";
import { makeStyles, mergeClasses, Button, Input, MenuList, MenuItemRadio, } from "@fluentui/react-components";
import { iconRegularClassName } from "@fluentui/react-icons";
import type { InputOnChangeData, MenuCheckedValueChangeEvent, MenuCheckedValueChangeData, } from "@fluentui/react-components";
import CategoryIcon from "owa-categories/lib/components/CategoryIcon";
import getMasterCategoryList from "owa-categories/lib/utils/getMasterCategoryList";
import type { MailboxInfo } from "owa-client-types";
import { getDensityModeCssClass } from "owa-fabric-theme";
import type { TableView } from "owa-mail-list-store";
import { type SortColumn } from "owa-mail-list-store";

import { getJumpToCategoryOptions } from "../helpers/getJumpToCategoryOptions";
import { getJumpToSizeOptions } from "../helpers/getJumpToSizeOptions";
import { getJumpToImportanceOptions } from "../helpers/getJumpToImportanceOptions";
import executeJumpTo from "../orchestrators/executeJumpTo";
import type { JumpToEntryPoint } from "../orchestrators/executeJumpTo";
import getJumpToSortInfo from "../utils/getJumpToSortInfo";
import isJumpToTextInputSort from "../utils/isJumpToTextInputSort";
import { goButtonLabel, goButtonLabelDateTimeSort, jumpToSenderInputPlaceholder, jumpToSubjectInputPlaceholder, jumpToRecipientInputPlaceholder, } from "./MailListJumpToPopover.locstring.json";
import { popoverSurfaceContainer, goButton, jumpToInput, checkmarkDisplay, categoryMenuScrollContainer, categoryMenuContainer, categoryIcon, popoverSurfaceText, full, } from "./MailListJumpToPopover.scss";

import { LazyJumpToDateCalloutContent } from "../lazyComponents";

import classnames from "owa-classnames";

export interface JumpToCalloutContentProps {
    tableView: TableView;
    mailboxInfo: MailboxInfo;
    onClose: () => void;
    onKeyDownCallback: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    entryPoint: JumpToEntryPoint;
}

const checkmarkProps = { className: checkmarkDisplay };

const useStyles = makeStyles({
    menuItem: {
        maxWidth: "unset",
        minWidth: "max-content",
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&:hover": {
            [`& .${iconRegularClassName}`]: {
                display: "inline-flex !important",
            },
        },
    },
});

const defaultExport: React.FunctionComponent<JumpToCalloutContentProps> = observer(function JumpToCalloutContent(props: JumpToCalloutContentProps) {
    const { mailboxInfo, tableView, onClose, onKeyDownCallback, inputRef, entryPoint } = props;
    const [inputValue, setInputValue] = useState("");
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [isDateComponentReady, setIsDateComponentReady] = useState(false);
    const goButtonRef = React.useRef<HTMLButtonElement>(null);
    const isComposingRef = React.useRef(false);
    React.useLayoutEffect(() => {
        inputRef.current?.focus();
    }, [inputRef]);
    const onCompositionStart = React.useCallback(() => {
        isComposingRef.current = true;
    }, []);
    const onCompositionEnd = React.useCallback(() => {
        isComposingRef.current = false;
    }, []);
    const sortInfo = getJumpToSortInfo(tableView);
    const sortColumn = sortInfo?.sortColumn;
    const sortDirection = sortInfo?.sortDirection;
    const categoryList = getMasterCategoryList(mailboxInfo);
    const styles = useStyles();
    const densityMode = getDensityModeCssClass(full, undefined, undefined);
    const isDateTimeSort = sortColumn === 1;
    const inputPlaceholder = isJumpToTextInputSort(sortColumn)
        ? loc(sortColumn === 3
            ? jumpToSenderInputPlaceholder
            : sortColumn === 8
                ? jumpToSubjectInputPlaceholder
                : jumpToRecipientInputPlaceholder)
        : "";
    
    // Show the Go button when the user needs to input some data and then trigger the jump.
    // For the sort columns where we have a dropdown, we don't need the Go button because we trigger the jump on selection.
    const shouldShowGoButton = React.useMemo(() => {
        return sortColumn === 1 || isJumpToTextInputSort(sortColumn);
    }, [sortColumn]);
    
    // Focus the Go button when the callout opens only in DateTime sort, and only after the date component is ready
    React.useEffect(() => {
        if (isDateTimeSort &&
            shouldShowGoButton &&
            isDateComponentReady &&
            goButtonRef.current) {
            goButtonRef.current?.focus();
        }
    }, [isDateTimeSort, shouldShowGoButton, isDateComponentReady]);
    
    const onGoClicked = React.useCallback(() => {
        if (!tableView.isLoading && inputValue.trim() !== "") {
            onClose();
            executeJumpTo(tableView, inputValue /*restrictionValue*/, entryPoint);
        }
    }, [inputValue, onClose, tableView, entryPoint]);
    
    const setInputValueCallback = React.useCallback((input: string) => {
        setInputValue(input);
    }, []);
    
    const onSelectedDateChange = React.useCallback((date: string | null) => {
        setSelectedDate(date);
    }, []);
    
    const onDateComponentReady = React.useCallback(() => {
        setIsDateComponentReady(true);
    }, []);
    
    const handleInputChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.stopPropagation();
        if (data.value !== undefined) {
            setInputValue(data.value);
        }
        else {
            setInputValue("");
        }
    }, []);
    
    const onCheckedValueChange = React.useCallback((ev: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
        ev.stopPropagation();
        onClose();
        const checkedItem = data.checkedItems?.[0];
        if (checkedItem !== undefined) {
            executeJumpTo(tableView, checkedItem /*restrictionValue*/, entryPoint);
        }
    }, [onClose, tableView, entryPoint]);
    
    const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        // Check tracked composition state and the native flag; legacy IME keyCode 229
        // covers events where that flag is false, keeping Enter/Escape with the IME.
        if (isComposingRef.current ||
            event.nativeEvent.isComposing ||
            event.nativeEvent.keyCode === 229) {
            return;
        }
        if (event.key === "Enter") {
            onGoClicked();
            event.preventDefault();
            event.stopPropagation();
        }
        else if (event.key === "Escape") {
            onKeyDownCallback(event);
        }
    }, [onGoClicked, onKeyDownCallback]);
    
    const getCategoryIcon = React.useCallback((categoryName: string) => {
        return (<CategoryIcon categoryName={categoryName} iconClassName={categoryIcon} categoryList={categoryList} mailboxInfo={mailboxInfo}/>);
    }, [categoryList, mailboxInfo]);
    
    const inputForSortColumn = React.useMemo((): React.JSX.Element | null => {
        if (isJumpToTextInputSort(sortColumn)) {
            return (<Input ref={inputRef} className={classnames(jumpToInput, popoverSurfaceText, densityMode)} placeholder={inputPlaceholder} onChange={handleInputChange} onKeyDown={onKeyDown} onCompositionStart={onCompositionStart} onCompositionEnd={onCompositionEnd}/>);
        }
        switch (sortColumn) {
            case 5:
                return (<MenuList onCheckedValueChange={onCheckedValueChange}>
                            {getJumpToImportanceOptions().map(option => {
                        return (<MenuItemRadio key={option.value} className={mergeClasses(styles.menuItem, popoverSurfaceText, densityMode)} checkmark={checkmarkProps} name="importance" value={option.value}>
                                        {loc(option.text)}
                                    </MenuItemRadio>);
                    })}
                        </MenuList>);
            case 1:
                return (<LazyJumpToDateCalloutContent tableView={tableView} onCheckedValueChange={onCheckedValueChange} setInputValue={setInputValueCallback} onSelectedDateChange={onSelectedDateChange} onComponentReady={onDateComponentReady}/>);
            case 14:
                return (<div className={classnames(categoryMenuScrollContainer, "customScrollBar")}>
                            <MenuList className={categoryMenuContainer} onCheckedValueChange={onCheckedValueChange}>
                                {getJumpToCategoryOptions(categoryList).map(option => {
                        return (<MenuItemRadio key={option.value} checkmark={checkmarkProps} name="category" value={option.value} className={mergeClasses(styles.menuItem, popoverSurfaceText, densityMode)}>
                                            <>
                                                {getCategoryIcon(option.value)}
                                                {option.text}
                                            </>
                                        </MenuItemRadio>);
                    })}
                            </MenuList>
                        </div>);
            case 7:
                return (<MenuList onCheckedValueChange={onCheckedValueChange}>
                            {getJumpToSizeOptions(sortDirection ?? "Ascending").map(option => {
                        return (<MenuItemRadio key={option.value} className={mergeClasses(styles.menuItem, popoverSurfaceText, densityMode)} checkmark={checkmarkProps} name="size" value={option.value.toString()}>
                                        {loc(option.text)}
                                    </MenuItemRadio>);
                    })}
                        </MenuList>);
            default:
                return null;
        }
    }, [
        sortColumn,
        sortDirection,
        tableView,
        onCheckedValueChange,
        onKeyDown,
        onSelectedDateChange,
        onDateComponentReady,
        inputRef,
        inputPlaceholder,
        onCompositionStart,
        onCompositionEnd,
    ]);
    
    return (<div className={popoverSurfaceContainer}>
                {inputForSortColumn}
                {shouldShowGoButton && (<Button ref={goButtonRef} disabled={tableView.isLoading || inputValue.trim() === ""} onClick={onGoClicked} aria-label={isDateTimeSort && selectedDate
                ? format(loc(goButtonLabelDateTimeSort), selectedDate)
                : loc(goButtonLabel)} className={classnames(goButton, popoverSurfaceText, densityMode)}>
                        {loc(goButtonLabel)}
                    </Button>)}
            </div>);
}, "JumpToCalloutContent");
export default defaultExport;
